import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Layout } from "../components/Layout";
import { ArrowRight, Plus, X } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const TYPES = ["job", "gig", "apprenticeship", "rfq"];

export default function NewOpportunity() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [skills, setSkills] = useState([]);
  const [type, setType] = useState("job");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [minExp, setMinExp] = useState(0);
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [remote, setRemote] = useState(false);
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/skills").then(({ data }) => setSkills(data.skills)); }, []);

  const toggle = (v) => setRequiredSkills(requiredSkills.includes(v) ? requiredSkills.filter((s) => s !== v) : [...requiredSkills, v]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/opportunities", {
        type, title, description,
        required_skills: requiredSkills, min_experience: Number(minExp),
        city, state: stateName, remote,
        budget_min: budgetMin ? Number(budgetMin) : null,
        budget_max: budgetMax ? Number(budgetMax) : null,
      });
      toast.success("Opportunity posted");
      nav(`/opportunities/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_occurred")); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("new_opportunity")}</h1>

        <form onSubmit={submit} className="as-card p-7 mt-8 space-y-5" data-testid="new-op-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="as-label">{t("op_type")}</label>
              <select className="as-input" value={type} onChange={(e) => setType(e.target.value)} data-testid="op-type">
                {TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>
            <div>
              <label className="as-label">{t("title")}</label>
              <input className="as-input" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="op-title" />
            </div>
          </div>
          <div>
            <label className="as-label">{t("description")}</label>
            <textarea className="as-input min-h-[120px]" required value={description} onChange={(e) => setDescription(e.target.value)} data-testid="op-desc" />
          </div>
          <div>
            <label className="as-label">{t("required_skills")}</label>
            <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
              {skills.map((s) => (
                <button key={s} type="button" onClick={() => toggle(s)}
                  className={`as-badge-skill ${requiredSkills.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}
                  data-testid={`op-skill-${s.toLowerCase().replace(/\s+/g,'-')}`}>
                  {requiredSkills.includes(s) ? <X size={12} weight="bold" /> : <Plus size={12} weight="bold" />} {s}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="as-label">{t("filter_min_exp")}</label>
              <input className="as-input" type="number" min={0} value={minExp} onChange={(e) => setMinExp(e.target.value)} data-testid="op-min-exp" />
            </div>
            <div>
              <label className="as-label">{t("city")}</label>
              <input className="as-input" value={city} onChange={(e) => setCity(e.target.value)} data-testid="op-city" />
            </div>
            <div>
              <label className="as-label">{t("state")}</label>
              <input className="as-input" value={stateName} onChange={(e) => setStateName(e.target.value)} data-testid="op-state" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} className="w-5 h-5" data-testid="op-remote" />
              <span className="font-bold text-[var(--as-primary)]">{t("remote")}</span>
            </label>
            <div>
              <label className="as-label">{t("budget")} min</label>
              <input className="as-input" type="number" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} data-testid="op-budget-min" />
            </div>
            <div>
              <label className="as-label">{t("budget")} max</label>
              <input className="as-input" type="number" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} data-testid="op-budget-max" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="op-submit">
              {busy ? "..." : t("post_job")} <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
